// Pure helpers for AdvancedMD insurance → rendering-provider mapping.
//
// A client assigns patients to a doctor AND a billing Tax ID based on the patient's
// insurance plan and age. This module holds the whole decision: normalize the free text
// read off the page, compute age from the date of birth, and match against a rule table
// supplied by the sync template.
//
// Side-effect free so the entire decision surface is unit-testable without a browser,
// and so it can be reused by the `resolveInsuranceProvider` action in useEHR.js.
//
// PHI: this module never logs. Callers log rule IDs and statuses, never the inputs.

/**
 * Normalize insurance free text for containment matching.
 * Uppercases, replaces every run of non-alphanumerics with a single space, trims.
 *
 * @param {*} raw
 * @returns {string} normalized text ("" for null/undefined/blank)
 */
export function normalizeInsuranceText(raw) {
  if (raw == null) return "";
  return String(raw)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

// Section labels that follow the carrier name inside the AdvancedMD insurance panel.
// Matched as whole tokens against normalized text, in panel order.
const PANEL_SECTION_MARKERS = ["POLICY", "FORMULARY PLAN", "LAST UPDATED"];

/**
 * Cut insurance-panel boilerplate off a normalized carrier read.
 *
 * The carrier source may fall back to the whole AdvancedMD insurance panel (div#A1), whose
 * text runs "PRIMARY INSURANCE <carrier + address> POLICY HOLDER <person> POLICY <number>
 * FORMULARY PLAN <plan> LAST UPDATED <date>". Everything from the POLICY section onward is
 * patient data, not carrier identity — a policy holder surnamed "Molina" must never match
 * the Molina carrier rules. Truncates at the first section marker; a clean carrier-only
 * read contains no markers and passes through unchanged.
 *
 * @param {string} normalizedText output of normalizeInsuranceText
 * @returns {string} normalized text with trailing panel sections removed
 */
function truncateAtPanelSections(normalizedText) {
  if (normalizedText === "") return "";
  const padded = " " + normalizedText + " ";
  let cut = padded.length;
  for (const marker of PANEL_SECTION_MARKERS) {
    const index = padded.indexOf(" " + marker + " ");
    if (index !== -1 && index < cut) cut = index;
  }
  return padded.slice(0, cut).trim();
}

/**
 * Extract the date of birth from the AdvancedMD patient-profile widget and compute age.
 *
 * The widget renders DOB, age, and sex on one line, e.g. "05-19-1960,66y,F". The
 * pre-rendered "66y" is deliberately ignored: it is a snapshot that can be stale relative
 * to the encounter date, and parsing the date is more robust than trusting a formatted
 * string. Accepts MM-DD-YYYY with "-" or "/" separators, plus ISO YYYY-MM-DD — the DOB
 * may come from the chat's patient record instead of the page, and records store either
 * "MM/DD/YYYY" or ISO. The two shapes cannot collide: a 4-digit leading year never
 * matches the month-first pattern.
 *
 * @param {*} raw       widget text or patient-record DOB
 * @param {Date} [asOf] date to compute age against (defaults to now)
 * @returns {number|null} whole years, or null if no valid past date could be parsed
 */
export function parseAgeFromWidget(raw, asOf) {
  if (raw == null) return null;
  const text = String(raw);

  let month;
  let day;
  let year;
  const widgetMatch = text.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (widgetMatch) {
    month = Number(widgetMatch[1]);
    day = Number(widgetMatch[2]);
    year = Number(widgetMatch[3]);
  } else {
    const isoMatch = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!isoMatch) return null;
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  }

  const dob = new Date(year, month - 1, day);
  // Reject impossible dates: JS Date rolls 13-45 over into a valid-looking date, so
  // verify the components survived the round-trip instead of trusting the constructor.
  if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) {
    return null;
  }

  const now = asOf instanceof Date ? asOf : new Date();
  if (dob > now) return null;

  let age = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

/**
 * Test a normalized string against a rule's alias list.
 *
 * Containment is word-boundary-aware, not a raw substring test: both sides are padded
 * with a leading/trailing space so an alias only matches whole tokens (e.g. "cigna" must
 * not match inside "CIGNAA HEALTH" — one character off is a different carrier, and these
 * rules select billing Tax IDs).
 *
 * @returns {boolean|null} true = matched, false = no match, null = unconstrained
 *                         (the rule places no requirement on this field)
 */
function matchAliases(normalizedText, aliases) {
  if (!Array.isArray(aliases) || aliases.length === 0) return null;
  const paddedText = " " + normalizedText + " ";
  for (const alias of aliases) {
    const normalizedAlias = normalizeInsuranceText(alias);
    if (normalizedAlias !== "" && paddedText.includes(" " + normalizedAlias + " ")) return true;
  }
  return false;
}

/** Half-open band test: [minAge, maxAge). null bounds are unbounded. */
function ageInBand(age, rule) {
  const min = rule.minAge == null ? 0 : rule.minAge;
  const max = rule.maxAge == null ? Infinity : rule.maxAge;
  return age >= min && age < max;
}

/** Span of a rule's age band — narrower is more specific. */
function ageSpan(rule) {
  const min = rule.minAge == null ? 0 : rule.minAge;
  const max = rule.maxAge == null ? Infinity : rule.maxAge;
  return max - min;
}

function emptyResolution(status) {
  return {
    status,
    provider: null,
    providers: [],
    taxId: null,
    billingEntity: null,
    ruleId: null,
    candidateRuleIds: [],
    message: null,
    note: null,
  };
}

/**
 * Resolve a rendering provider from carrier, plan, and age.
 *
 * Matching is normalized containment against explicit alias lists — deliberately NOT
 * fuzzy. These rules select billing Tax IDs, so a near-miss is a misrouted claim; failing
 * to match is recoverable (the biller fills the field by hand), matching wrong is not.
 *
 * Never throws, never guesses. Any uncertainty returns a status with provider === null,
 * and the caller writes nothing.
 *
 * @param {{carrier: *, plan: *, age: number|null}} inputs
 * @param {Array<object>} rules
 * @returns {object} resolution — see the module's JSDoc for the shape
 */
export function resolveProvider(inputs, rules) {
  const carrierText = truncateAtPanelSections(normalizeInsuranceText(inputs && inputs.carrier));
  const planText = normalizeInsuranceText(inputs && inputs.plan);
  const age = inputs ? inputs.age : null;

  if (carrierText === "") return emptyResolution("no_carrier");
  if (typeof age !== "number" || !Number.isFinite(age)) return emptyResolution("no_dob");
  if (!Array.isArray(rules)) return emptyResolution("no_match");

  const valid = rules.filter((rule) => rule && typeof rule === "object");

  // Rules whose carrier matches, regardless of plan or age — used both to find
  // candidates and to detect the "carrier is known but we couldn't read the plan" case.
  const carrierMatches = valid.filter((rule) => matchAliases(carrierText, rule.carrier) !== false);

  const candidates = [];
  for (const rule of carrierMatches) {
    const planResult = matchAliases(planText, rule.plan);
    if (planResult === false) continue;
    if (!ageInBand(age, rule)) continue;
    candidates.push({
      rule,
      planMatched: planResult === true ? 1 : 0,
      carrierMatched: matchAliases(carrierText, rule.carrier) === true ? 1 : 0,
      span: ageSpan(rule),
    });
  }

  if (candidates.length === 0) {
    // Distinguish "this carrier isn't on the sheet" from "this carrier needs a plan we
    // couldn't read" — the second is recoverable by fixing the plan read, the first isn't.
    const needsPlan = planText === "" && carrierMatches.some((rule) => Array.isArray(rule.plan) && rule.plan.length > 0);
    return emptyResolution(needsPlan ? "needs_plan" : "no_match");
  }

  // A carrier with plan-specific rules, where the plan wasn't read, cannot be resolved
  // safely even if a carrier-only rule happens to match — the plan-specific rule might
  // have been the right answer and routes to a different Tax ID.
  if (planText === "" && carrierMatches.some((rule) => Array.isArray(rule.plan) && rule.plan.length > 0)) {
    return emptyResolution("needs_plan");
  }

  // Most specific wins: plan match, then carrier match, then the narrower age band.
  candidates.sort((a, b) => {
    if (a.planMatched !== b.planMatched) return b.planMatched - a.planMatched;
    if (a.carrierMatched !== b.carrierMatched) return b.carrierMatched - a.carrierMatched;
    return a.span - b.span;
  });

  const best = candidates[0];
  const tied = candidates.filter(
    (c) => c.planMatched === best.planMatched && c.carrierMatched === best.carrierMatched && c.span === best.span
  );

  if (tied.length > 1) {
    const resolution = emptyResolution("ambiguous");
    resolution.candidateRuleIds = tied.map((c) => c.rule.id).filter(Boolean);
    return resolution;
  }

  const rule = best.rule;
  const providers = Array.isArray(rule.providers) ? rule.providers.filter(Boolean) : [];
  const base = {
    status: "matched",
    provider: null,
    providers,
    taxId: rule.taxId || null,
    billingEntity: rule.billingEntity || null,
    ruleId: rule.id || null,
    candidateRuleIds: [],
    message: rule.message || null,
    note: rule.note || null,
  };

  // Non-assignment rows (refusals, pending contracts) carry a message, never a provider.
  if (rule.outcome && rule.outcome !== "assign") {
    return { ...base, status: rule.outcome, providers: [] };
  }

  // Two valid doctors is a coin flip on a billing document — surface both, write neither.
  if (providers.length !== 1) {
    return { ...base, status: providers.length > 1 ? "multiple" : "no_match" };
  }

  return { ...base, provider: providers[0] };
}
